import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from './common/SafeIcon';
import RichTextEditor from './common/RichTextEditor';
import { useData } from '../context/DataContext';

const {
  FiArrowLeft, FiSave, FiCheckCircle,
  FiUser, FiBook, FiMail, FiFileText, FiInfo,
} = FiIcons;

// ── Field definitions ─────────────────────────────────────────────────────────
// richtext fields use RichTextEditor; all others use plain inputs.
const backMatterDefs = [
  {
    id: 'about_author',
    label: 'About the Author',
    icon: FiUser,
    description: 'A short biography that helps readers connect with you.',
    fields: [
      {
        key: 'bio',
        label: 'Author Bio',
        type: 'richtext',
        placeholder: 'Write your author bio here. Bold your name, italicise book titles, or add a link to your website...',
        minHeight: 'min-h-[160px]',
      },
      { key: 'website',      label: 'Website URL',              type: 'text',     placeholder: 'https://yourwebsite.com' },
      { key: 'social',       label: 'Social Media Handle(s)',   type: 'text',     placeholder: 'e.g. @yourhandle on Instagram & Facebook' },
      { key: 'photo_credit', label: 'Photo Credit (optional)',  type: 'text',     placeholder: 'e.g. Photo by Jane Smith Photography' },
    ],
  },
  {
    id: 'other_books',
    label: 'Other Books by the Author',
    icon: FiBook,
    description: 'List your other published titles to drive cross-sales.',
    fields: [
      {
        key: 'books_list',
        label: 'Book Titles & Links',
        type: 'richtext',
        placeholder: 'List each book — bold the title, add a hyperlink to Amazon or your website, italicise the subtitle...\n\nExample:\nGrace Upon Grace — link to amazon.com/dp/XXXXXX\nFaith Forward — link to yourwebsite.com/books',
        minHeight: 'min-h-[180px]',
      },
      {
        key: 'cta',
        label: 'Call to Action (optional)',
        type: 'text',
        placeholder: 'e.g. Available wherever ebooks are sold.',
      },
    ],
  },
  {
    id: 'newsletter',
    label: 'Newsletter / Reader Magnet Signup',
    icon: FiMail,
    description: 'Invite readers to join your email list with a free gift or exclusive content.',
    fields: [
      { key: 'headline',   label: 'Signup Headline',          type: 'text',     placeholder: 'e.g. Get Your Free 7-Day Devotional' },
      { key: 'body',       label: 'Signup Description',       type: 'textarea', placeholder: 'Describe what readers will receive when they sign up...' },
      { key: 'signup_url', label: 'Signup URL',               type: 'text',     placeholder: 'https://yoursite.com/freebie' },
      { key: 'free_gift',  label: 'Free Gift / Lead Magnet Name (optional)', type: 'text', placeholder: 'e.g. The Faithful Author Starter Kit' },
    ],
  },
  {
    id: 'notes',
    label: 'Notes / Acknowledgements',
    icon: FiFileText,
    description: 'Space for source notes, endnotes, or heartfelt acknowledgements.',
    fields: [
      { key: 'notes_type',       label: 'Section Type',          type: 'select',   options: ['Acknowledgements', 'Notes & References', 'Both'] },
      { key: 'acknowledgements', label: 'Acknowledgements Text', type: 'textarea', placeholder: 'Thank the people who helped make this book possible...' },
      { key: 'notes_body',       label: 'Notes / References',    type: 'textarea', placeholder: 'List chapter notes, scripture references, or bibliography here...' },
    ],
  },
];

// ── Component ─────────────────────────────────────────────────────────────────
const BackMatterLibrary = ({ onBack }) => {
  const { currentUser } = useData();
  const [content, setContent]   = useState({});
  const [saving, setSaving]     = useState(false);
  const [saved, setSaved]       = useState(false);
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    const load = async () => {
      if (!currentUser?.local_id) return;
      try {
        const res  = await fetch(`/api/back-matter.php?user_id=${currentUser.local_id}`);
        const data = await res.json();
        if (data.content) setContent(data.content);
      } catch (err) {
        console.error('Failed to load back matter:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [currentUser]);

  const handleField = (sectionId, key, value) => {
    setContent(prev => ({
      ...prev,
      [sectionId]: { ...(prev[sectionId] || {}), [key]: value },
    }));
    setSaved(false);
  };

  const handleSave = async () => {
    if (!currentUser?.local_id) return;
    setSaving(true);
    try {
      await fetch('/api/back-matter.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: currentUser.local_id, content }),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error('Failed to save back matter:', err);
    } finally {
      setSaving(false);
    }
  };

  const renderField = (section, field) => {
    const sectionData = content[section.id] || {};
    const val = sectionData[field.key] || '';

    if (field.type === 'richtext') {
      return (
        <div key={field.key}>
          <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">
            {field.label}
          </label>
          <RichTextEditor
            value={val}
            onChange={(html) => handleField(section.id, field.key, html)}
            placeholder={field.placeholder}
            minHeight={field.minHeight}
          />
          <p className="text-[10px] text-slate-400 mt-1.5 font-medium">
            Supports <strong>bold</strong>, <em>italic</em>, and hyperlinks. HTML is preserved in the EPUB export.
          </p>
        </div>
      );
    }

    if (field.type === 'textarea') {
      return (
        <div key={field.key}>
          <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">
            {field.label}
          </label>
          <textarea
            value={val}
            onChange={(e) => handleField(section.id, field.key, e.target.value)}
            placeholder={field.placeholder}
            className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary resize-y min-h-[120px]"
          />
        </div>
      );
    }

    if (field.type === 'select') {
      return (
        <div key={field.key}>
          <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">
            {field.label}
          </label>
          <select
            value={val}
            onChange={(e) => handleField(section.id, field.key, e.target.value)}
            className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary bg-white"
          >
            <option value="">Select type...</option>
            {field.options.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        </div>
      );
    }

    // Default: plain text input
    return (
      <div key={field.key}>
        <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">
          {field.label}
        </label>
        <input
          type="text"
          value={val}
          onChange={(e) => handleField(section.id, field.key, e.target.value)}
          placeholder={field.placeholder}
          className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
        />
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-cream p-6 md:p-12 relative overflow-hidden">
      <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-primary/5 rounded-full blur-[120px]" />
      <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-secondary/5 rounded-full blur-[120px]" />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-4xl mx-auto relative z-10"
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-10">
          <button
            onClick={onBack}
            className="flex items-center space-x-2 text-slate-500 hover:text-primary transition-colors text-xs font-bold uppercase tracking-widest"
          >
            <SafeIcon icon={FiArrowLeft} />
            <span>Dashboard</span>
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className={`flex items-center space-x-2 px-6 py-3 rounded-xl font-bold text-sm uppercase tracking-widest transition-all shadow-lg
              ${saved ? 'bg-green-500 text-white' : 'bg-primary hover:bg-blue-800 text-white'} disabled:opacity-50`}
          >
            <SafeIcon icon={saved ? FiCheckCircle : FiSave} />
            <span>{saving ? 'Saving...' : saved ? 'Saved!' : 'Save Back Matter'}</span>
          </button>
        </div>

        <div className="mb-8">
          <div className="inline-flex items-center space-x-2 px-4 py-2 bg-primary/10 rounded-full text-primary text-xs font-bold uppercase tracking-widest mb-4">
            <SafeIcon icon={FiFileText} />
            <span>Back Matter Library</span>
          </div>
          <h1 className="text-4xl font-serif text-primary mb-3 tracking-tight">Author Back Matter</h1>
          <p className="text-slate-600 text-lg font-medium max-w-2xl">
            Write your back matter content once. Every EPUB export will automatically pull this content when those sections are toggled on during conversion.
          </p>
        </div>

        <div className="bg-blue-50 border border-blue-100 rounded-2xl p-5 flex items-start space-x-3 mb-8">
          <SafeIcon icon={FiInfo} className="text-blue-500 mt-0.5 flex-shrink-0 text-lg" />
          <div>
            <p className="text-sm text-blue-800 font-bold mb-1">Rich Text Support</p>
            <p className="text-sm text-blue-700">
              The <strong>Author Bio</strong> and <strong>Book Titles &amp; Links</strong> fields support bold, italic, and clickable hyperlinks.
              Select text and use the toolbar — or <kbd className="bg-blue-100 px-1.5 py-0.5 rounded font-mono text-xs">Ctrl+B</kbd> / <kbd className="bg-blue-100 px-1.5 py-0.5 rounded font-mono text-xs">Ctrl+I</kbd> — to format.
              Links open in a new tab when readers click them in compatible e-readers. All formatting is preserved in your EPUB export.
            </p>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : (
          <div className="space-y-6">
            {backMatterDefs.map((section, idx) => (
              <motion.div
                key={section.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.06 }}
                className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden"
              >
                {/* Section Header */}
                <div className="flex items-center space-x-4 px-7 py-5 bg-slate-50 border-b border-slate-100">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                    <SafeIcon icon={section.icon} className="text-lg" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-800 text-base">{section.label}</h3>
                    <p className="text-xs text-slate-500 mt-0.5">{section.description}</p>
                  </div>
                </div>

                {/* Fields */}
                <div className="p-7 space-y-5">
                  {section.fields.map((field) => renderField(section, field))}
                </div>
              </motion.div>
            ))}

            {/* Bottom Save */}
            <div className="flex justify-end pt-4 pb-8">
              <button
                onClick={handleSave}
                disabled={saving}
                className={`flex items-center space-x-2 px-8 py-4 rounded-xl font-bold text-sm uppercase tracking-widest transition-all shadow-lg
                  ${saved ? 'bg-green-500 text-white' : 'bg-primary hover:bg-blue-800 text-white'} disabled:opacity-50`}
              >
                <SafeIcon icon={saved ? FiCheckCircle : FiSave} />
                <span>{saving ? 'Saving...' : saved ? 'Saved!' : 'Save Back Matter Library'}</span>
              </button>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default BackMatterLibrary;